<script lang="ts" setup>
import { SelectionModeEnum } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import { SearchMode } from "~/core/composables/useSearch";
import { useToggle } from "~/core/composables/useToggle";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import {
  NotificationKey,
  setDetailMessageAsListItem,
  showNotificationError,
  showNotificationSuccess,
  showNotificationWarning,
} from "~/core/utils/notifications";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useAttachDetachEntitiesToSector } from "~/modules/sector/composables/useAttachDetachEntitiesToSector";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import type { SectorAttachDetachPayload } from "~/modules/sector/models/SectorAttachDetachPayload.interface";
import type { SectorItemActionResult } from "~/modules/sector/models/SectorItemActionResult.interface";
import type { SelectedItemsType } from "~/modules/sector/models/SelectedItems.type";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { useFetchUserGroups } from "~/modules/user/composables/useFetchUserGroups";
import { useFetchUsers } from "~/modules/user/composables/useFetchUsers";
import { Filters, SectorResource } from "~/types";
import ILxcArrowUpLeft from "~icons/lxc/arrow-up-left";
import ILxcPlus from "~icons/lxc/plus";

const props = defineProps<{
  primaryKey: string;
  sector: Sector;
  selectedItems: SelectedItemsType;
  resource: SectorResource;
}>();
const emit = defineEmits(["change"]);
defineExpose({
  openDetachModal,
});

const { sectors } = storeToRefs(useSectorStore());

const { t } = useI18n();
const [attachModalVisible, toggleAttachModalVisible] = useToggle();
const [detachModalVisible, toggleDetachModalVisible] = useToggle();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const {
  error: errorAttachDetach,
  results: resultsAttachDetach,
  attachDetachEntities,
} = useAttachDetachEntitiesToSector(currentOrganization.value?.code);

function getSearchComposable() {
  switch (props.resource) {
    case SectorResource.DTWINS:
      return useFetchDtwins;
    case SectorResource.USER_GROUP:
      return useFetchUserGroups;
    case SectorResource.USER:
      return useFetchUsers;
  }
}

const useSearch = getSearchComposable();
const {
  itemType,
  isLoading,
  results,
  error,
  getDescription,
  filters,
  fetchData,
  setFilter,
  canManageItems,
  getItemIds,
} = useSearch!({
  organizationCode: currentOrganization.value?.code,
  sectorCode: props.sector.code,
  outSector: true,
  searchMode: SearchMode.FILTER_SEARCH,
});

const attachSelectedItems = ref<SelectedItemsType>([]);
const detachSelectedItems = ref<SelectedItemsType>([]);
const displayActions = computed(() => canManageItems(props.sector));
const detachDisabled: ComputedRef<boolean> = computed(
  () => !props.selectedItems?.length,
);

const filterKey = computed(() => {
  switch (props.resource) {
    case SectorResource.DTWINS:
      return Filters.DTWIN_NAME_OR_SERIAL_NUMBER;
    case SectorResource.USER:
      return Filters.USER_FIRST_OR_LAST_NAME;
    default:
      return Filters.NAME;
  }
});

const searchQuery = computed({
  get() {
    return (filters.get(filterKey.value) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(filterKey.value, searchQuery);
  },
});

/**
 * Fetch items
 * @param page
 * @param pageSize
 */
async function fetchItems(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  const params = new Map();

  if (sectors.value?.code && sectors.value?.code !== props.sector.code) {
    setFilter(Filters.SECTORS, [sectors.value.code]);
  }
  setFilter(Filters.SECTORS_EXCLUDED, [props.sector.code]);

  await fetchData(page, pageSize, params);
}

/**
 * Attach item(s) to the sector
 */
async function onAttachToSector() {
  if (attachSelectedItems.value.length > 0) {
    const attachEntitiesPayload: SectorAttachDetachPayload = {
      assign: getItemIds(attachSelectedItems.value),
      unassign: [],
    };
    await attachDetachEntities(
      props.sector.code,
      props.resource,
      attachEntitiesPayload,
    );
    if (errorAttachDetach.value) {
      errorAttachDetach.value.notify(NotificationKey.error);
    } else {
      const responseWithItemDescription = resultsAttachDetach.value.map(
        (action) => ({
          ...action,
          name: getDescription(
            attachSelectedItems.value.find(
              (item) => item[props.primaryKey] === action.name,
            ),
          ),
        }),
      );
      displayActionStatus(
        responseWithItemDescription,
        `${NotificationKey.attachSuccess}.${itemType}`,
        `${NotificationKey.attachError}.${itemType}`,
      );
    }

    clearSelectionAndCloseAttachModal();
    emit("change");
  } else {
    showNotificationWarning(t("sectors.modals.attach.errors.list_empty"));
  }
}

function clearSelectionAndCloseAttachModal() {
  toggleAttachModalVisible(false);
  attachSelectedItems.value = [];
}

function clearSelectionAndCloseDetachModal() {
  toggleDetachModalVisible(false);
  detachSelectedItems.value = [];
}

/**
 * Detach item(s) from the sector
 */
async function detachFromSector() {
  if (detachSelectedItems.value.length > 0) {
    const detachEntitiesPayload: SectorAttachDetachPayload = {
      assign: [],
      unassign: getItemIds(detachSelectedItems.value),
    };
    await attachDetachEntities(
      props.sector.code,
      props.resource,
      detachEntitiesPayload,
    );
    if (errorAttachDetach.value) {
      errorAttachDetach.value.notify(NotificationKey.error);
    } else {
      const responseWithItemDescription = resultsAttachDetach.value.map(
        (action) => ({
          ...action,
          name: getDescription(
            detachSelectedItems.value.find(
              (item) => item[props.primaryKey] === action.name,
            ),
          ),
        }),
      );
      displayActionStatus(
        responseWithItemDescription,
        `${NotificationKey.detachSuccess}.${itemType}`,
        `${NotificationKey.detachError}.${itemType}`,
      );
    }

    clearSelectionAndCloseDetachModal();
    emit("change");
  } else {
    showNotificationWarning(
      t("sectors.modals.confirmDetach.errors.list_empty"),
    );
  }
}

/**
 * Show Action Status
 * @param data
 * @param successMessage
 * @param errorMessage
 */
function displayActionStatus(
  data: SectorItemActionResult[],
  successMessage: string,
  errorMessage: string,
) {
  const { notificationNumber: nbSuccess, msg: success } = getByStatus(
    data,
    true,
  );
  const { notificationNumber: nbError, msg: errors } = getByStatus(data, false);

  if (nbError) {
    showNotificationError(
      t(errorMessage, { sectorName: props.sector.name }, nbError),
      errors,
    );
  }

  if (nbSuccess > 0) {
    showNotificationSuccess(
      t(successMessage, { sectorName: props.sector.name }, nbSuccess),
      success,
    );
  }
}

function getByStatus(
  data: SectorItemActionResult[],
  success: boolean,
): { msg: string; notificationNumber: number } {
  const notificationsByStatus = data.filter((d) => d.success === success);

  const msg = notificationsByStatus
    .map((d) => d.name)
    .filter(Boolean)
    .map(setDetailMessageAsListItem)
    .join("");

  return {
    msg,
    notificationNumber: notificationsByStatus.length,
  };
}

/**
 * Open Attach modal, fetch items before
 */
async function openAttachModal() {
  toggleAttachModalVisible();
  await fetchItems();
}

/**
 * Open detach modal, accept single or multiple item as arg
 * @param data
 */
function openDetachModal(data: SelectedItemsType) {
  detachSelectedItems.value = data;
  toggleDetachModalVisible();
}
</script>

<template>
  <div class="flex" style="justify-content: flex-end">
    <lxc-button
      v-if="displayActions"
      class="self-center"
      html-type="button"
      type="primary"
      :icon="ILxcPlus"
      :title="t(`sectors.details.buttons.attach.${resource}`)"
      @click="openAttachModal"
    >
      {{ t(`sectors.details.buttons.attach.${resource}`) }}
    </lxc-button>

    <lxc-button
      v-if="displayActions"
      :disabled="detachDisabled"
      class="self-center ml-4"
      html-type="button"
      type="secondary"
      :title="t('sectors.details.buttons.detach')"
      :icon="ILxcArrowUpLeft"
      @click="openDetachModal(selectedItems)"
    >
      {{ t("sectors.details.buttons.detach") }}
    </lxc-button>
  </div>

  <lxc-common-modal
    :show="attachModalVisible"
    class="!min-w-[40%] !max-w-4xl"
    body-class="!max-w-4xl"
    @close="clearSelectionAndCloseAttachModal"
  >
    <template #header>
      <h3>{{ t(`sectors.modals.attach.${resource}.title`) }}</h3>
    </template>
    <template #body>
      <div>
        <p class="modal-description">
          {{ t(`sectors.modals.attach.${resource}.description`) }}
        </p>
        <search-bar
          v-model:search-query="searchQuery"
          class="search-bar"
          :search-placeholder="t('label.search')"
          @clear="fetchItems"
          @search="fetchItems"
        />
        <container-component
          :px="0"
          :is-loading="isLoading && !results"
          :error="error"
        >
          <sectors-details-table
            v-model="attachSelectedItems"
            :primary-key="primaryKey"
            :resource="resource"
            :is-loading="isLoading"
            :display-actions="false"
            :row-click-selection-mode="SelectionModeEnum.FORCE"
            :selection="true"
            :items="results"
            :get-description="getDescription"
            @pagination:get-next-page="fetchItems"
          >
            <slot />
          </sectors-details-table>
        </container-component>
      </div>
    </template>
    <template #footer>
      <div class="flex grow shrink-0 justify-end gap-4">
        <lxc-button
          type="secondary"
          html-type="button"
          :title="t('button.cancel')"
          @click="clearSelectionAndCloseAttachModal"
        >
          {{ t("button.cancel") }}
        </lxc-button>

        <lxc-button
          type="primary"
          html-type="button"
          :title="t('button.confirm')"
          @click="onAttachToSector"
        >
          {{ t("button.confirm") }}
        </lxc-button>
      </div>
    </template>
  </lxc-common-modal>

  <lxc-common-modal
    :show="detachModalVisible"
    class="!min-w-[40%] !max-w-4xl"
    body-class="!max-w-4xl"
    @close="clearSelectionAndCloseDetachModal"
  >
    <template #header>
      <h3>{{ t(`sectors.modals.confirmDetach.${resource}.title`) }}</h3>
    </template>
    <template #body>
      <div>
        <information-row
          :title="
            t(
              `sectors.modals.confirmDetach.${props.resource}.warning`,
              detachSelectedItems.length,
            )
          "
        />
        <p class="update-description">
          {{
            t(
              `sectors.modals.confirmDetach.${props.resource}.description`,
              {
                sectorLabel: sector.name,
                name: getDescription(detachSelectedItems[0]),
              },
              detachSelectedItems.length,
            )
          }}
        </p>
        <ul v-if="detachSelectedItems.length > 1">
          <li v-for="item in detachSelectedItems" :key="item">
            {{ getDescription(item) }}
          </li>
        </ul>
      </div>
    </template>
    <template #footer>
      <div class="flex grow shrink-0 justify-end gap-4">
        <lxc-button
          type="secondary"
          html-type="button"
          :title="t('button.cancel')"
          @click="clearSelectionAndCloseDetachModal"
        >
          {{ t("button.cancel") }}
        </lxc-button>

        <lxc-button
          type="primary"
          html-type="button"
          :title="t('button.confirm')"
          @click="detachFromSector"
        >
          {{ t("button.confirm") }}
        </lxc-button>
      </div>
    </template>
  </lxc-common-modal>
</template>
